import React, { useState } from 'react';
import { UploadIcon, XIcon, ImageIcon, LoaderIcon } from 'lucide-react';
import type { ImageBlock } from '../../../lib/types/template.types';
import { api } from '../../lib/apiClient';

interface ImageBlockEditorProps {
  block: ImageBlock;
  onChange: (block: ImageBlock) => void;
}

export function ImageBlockEditor({ block, onChange }: ImageBlockEditorProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      // Get image dimensions before upload
      const previewUrl = URL.createObjectURL(file);
      const img = new Image();
      img.src = previewUrl;
      await new Promise((resolve) => {
        img.onload = resolve;
      });

      const width = img.width;
      const height = img.height;

      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);

      // Get access token for authentication
      const { getAccessToken } = await import('../../lib/supabase');
      const token = await getAccessToken();

      // Upload image to backend
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/templates/upload-image', {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const result = await response.json();

      // Update block with storage path
      onChange({
        ...block,
        storage_url: result.storage_path,
        storage_path: result.storage_path,
        width,
        height,
      });
    } catch (error) {
      console.error('Image upload error:', error);
      setUploadError(error instanceof Error ? error.message : 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    onChange({
      ...block,
      storage_url: '',
      width: undefined,
      height: undefined,
    });
  };

  return (
    <div className="space-y-3">
      {/* Image Upload/Preview */}
      {!block.storage_url ? (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#5B8E87] transition-colors">
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileSelect}
            disabled={uploading}
            className="hidden"
            id="image-upload"
          />
          <label
            htmlFor="image-upload"
            className="cursor-pointer flex flex-col items-center gap-2"
          >
            {uploading ? (
              <>
                <LoaderIcon className="w-8 h-8 text-[#5B8E87] animate-spin" />
                <p className="text-sm text-gray-600">Uploading image...</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                  <UploadIcon className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Click to upload image
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    JPEG, PNG, GIF, or WebP (max 5MB)
                  </p>
                </div>
              </>
            )}
          </label>
        </div>
      ) : (
        <div className="relative group">
          <img
            src={block.storage_url}
            alt="Template image"
            className="w-full rounded-lg border border-gray-300"
          />
          <button
            type="button"
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg opacity-0 group-hover:opacity-100"
          >
            <XIcon className="w-4 h-4" />
          </button>
          {block.width && block.height && (
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-black bg-opacity-60 text-white text-xs rounded">
              {block.width} × {block.height}
            </div>
          )}
        </div>
      )}

      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <XIcon className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{uploadError}</p>
        </div>
      )}

      {/* Caption Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Caption
          <span className="text-gray-400 text-xs ml-1">(optional)</span>
        </label>
        <input
          type="text"
          value={block.caption || ''}
          onChange={(e) => onChange({ ...block, caption: e.target.value })}
          placeholder="Add a caption for this image..."
          className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
        />
      </div>
    </div>
  );
}
